import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreModule, DestroyableComponent, DialogComponent, formatPrice, IvvTitleComponent, OffersType } from '@toevla-webapplications/core';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable, switchMap, take, takeUntil } from 'rxjs';
import { OffersService } from '../../services/offers/offers.service';
import { Offer } from '../../services/offers/offer.model';
import { User } from '../../services/user/user.model';
import { Store } from '@ngrx/store';
import { IvvaStore } from '../../store/store.model';
import { FavoritesService } from '../../services/favorites/favorites.service';
import { ToastsService } from '../../services/toasts/toasts.service';
import { OfferCardComponent } from '../home/components/offer-card/offer-card.component';
import { NewsletterComponent } from '../../components/newsletter/newsletter.component';
import { HelpComponent } from '../../components/help/help.component';
import { environment } from '../../../environments/env.uat';
import { ImagesHeaderComponent } from './components/images-header/images-header.component';

@Component({
	selector: 'ivva-detail',
	templateUrl: './detail.component.html',
	styleUrls: ['./detail.component.scss'],
	imports: [CoreModule, TranslateModule, IvvTitleComponent, DialogComponent, OfferCardComponent, NewsletterComponent, HelpComponent, ImagesHeaderComponent],
})
export class DetailComponent extends DestroyableComponent implements OnInit {
	id: string;
	backRoute: string[] = ['/'];
	link: string;

	favorites$: Observable<number>;

	offer = signal<Offer | null>(null);
	addressCity = computed(() => {
		return this.getAddress(this.offer(), 'city');
	});
	addressFull = computed(() => {
		return this.getAddress(this.offer(), 'full');
	});
	addressStreet = computed(() => {
		return this.getAddress(this.offer(), 'street');
	});
	isFavorite = signal(false);
	nearbyOffers = signal<Offer[]>([]);
	showPriceInfo = signal(false);
	showShareDialog = signal(false);
	showSignInDialog = signal(false);
	user = signal<User | null>(null);

	priceBasedRentalUnit = computed(() => {
		const offer = this.offer();
		const unitId = offer?.priceBasedRentalUnitId;

		if (offer?.rentalunits) {
			return offer.rentalunits.find((unit) => unit.id === unitId) || null;
		}

		return null;
	});
	pricingVisible = computed(() => {
		const u = this.user();
		return this.offer()?.pricePublished || u?.eligibleForDiscount || u?.socialOrg;
	});

	readonly formatPrice = formatPrice;
	readonly OffersType = OffersType;
	readonly RRN_APP_URL = environment.rrnAppUrl;

	private readonly favoritesService = inject(FavoritesService);
	private readonly offersService = inject(OffersService);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly store = inject(Store<IvvaStore>);
	private readonly toastsService = inject(ToastsService);

	ngOnInit() {
		this.favorites$ = this.favoritesService.favorites$.pipe(map((list) => list.length));

		this.store
			.select('offers', 'user')
			.pipe(takeUntil(this.destroyed$))
			.subscribe((u) => this.user.set(u));

		this.route.paramMap
			.pipe(
				takeUntil(this.destroyed$),
				switchMap((params) => {
					this.scrollToTop();
					this.id = params.get('id') as string;
					return this.offersService.getOffer(this.id);
				})
			)
			.subscribe((offer) => {
				this.link = `https://${location.host}/offers/${offer.id}`;
				this.offer.set(offer);

				this.favoritesService.favorites$.pipe(takeUntil(this.destroyed$)).subscribe((favorites) => {
					const isFavorite = favorites.find((f) => f.id === this.offer()?.id);
					this.isFavorite.set(!!isFavorite);
				});

				if (offer.type !== OffersType.ORGANIZED_HOLIDAY) {
					this.offersService
						.getOffersNearby(OffersType.DAYTRIP, offer.location.lat, offer.location.lon, offer.type === OffersType.DAYTRIP)
						.subscribe((data) => this.nearbyOffers.set(data));
				}
			});

		this.route.data.pipe(take(1)).subscribe((data) => {
			if (data['backRoute']) {
				this.backRoute = data['backRoute'];
			}
		});
	}

	toggleFavorite() {
		const o = this.offer();

		if (o) {
			if (this.isFavorite()) {
				this.favoritesService.remove(o);
			} else {
				this.favoritesService.add(o);
			}
		}
	}

	convertWebsite(href: string): string {
		if (href.startsWith('http')) {
			return href;
		}

		return `https://${href}`;
	}

	signIn() {
		const offer = this.offer();
		if (offer) {
			if (offer.pricePublished) {
				this.router.navigate(['/', 'offers', offer.id, 'booking']);
			} else {
				this.saveRedirect(document.location.pathname);
				document.location.href = '/acmidm';
			}
		}
	}

	copyLink() {
		navigator.clipboard
			.writeText(this.link)
			.then(() => {
				this.toastsService.success('TOAST.SUCCESS', 'TOAST.COPY_PAGE_LINK');
				this.showShareDialog.set(false);
			})
			.catch(() => this.toastsService.error('TOAST.FAILED_TO_COPY_PAGE_LINK', 'TOAST.FAILED_TO_COPY_PAGE_LINK_MESSAGE'));
	}

	private getAddress(offer: Offer | null, format: 'full' | 'street' | 'city' = 'full'): string {
		if (!offer) {
			return '';
		}

		const no = offer.number ? ` ${offer.number}${offer.box ? ` (${offer.box})` : ''}` : '';

		if (format === 'street') {
			return `${offer.street}${no}`;
		} else if (format === 'city') {
			return `${offer.postalcode} ${offer.city}`;
		}

		return `${offer.street}${no}, ${offer.postalcode} ${offer.city}`;
	}

	private saveRedirect(link: string) {
		const data = JSON.stringify({ time: Date.now(), link });
		localStorage.setItem('redirect', data);
	}

	private scrollToTop() {
		window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
	}
}
